/**
 * PromoteMemoryModal — widen a memory's scope. Demo page: /ui-elements/dialogs; form fields from /form-layouts.
 */
import React from 'react';
import { Form, FormActions, SelectFormField, TextFormField, useZodForm } from '../../../components/form/index.js';
import { Modal } from '../../../components/ui/index.js';
import { zMemoryPromoteRequest } from '../../../generated/api/zod.gen.js';
import { MEMORY_PROMOTION_TARGETS, MEMORY_SCOPE_LABEL, type MemoryScope } from './memory-scope.js';

const promoteSchema = zMemoryPromoteRequest.pick({ targetScope: true, newSubjectId: true });

interface PromoteMemoryModalProps {
  open: boolean;
  currentScope: MemoryScope;
  onClose: () => void;
  /** Throws on failure so the form places the error on its field. */
  onPromote: (targetScope: MemoryScope, newSubjectId: string) => Promise<unknown>;
}

export const PromoteMemoryModal: React.FC<PromoteMemoryModalProps> = ({ open, currentScope, onClose, onPromote }) => {
  const targets = MEMORY_PROMOTION_TARGETS[currentScope];
  const form = useZodForm(promoteSchema, {
    defaultValues: { targetScope: targets[0] ?? 'org', newSubjectId: '' },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Promote memory"
      description="Promote this memory to a wider scope so more agents and people can see it."
    >
      <Form form={form} onSubmit={(values) => onPromote(values.targetScope, values.newSubjectId)}>
        <SelectFormField
          name="targetScope"
          label="Target scope"
          required
          options={targets.map((t) => ({ value: t, label: MEMORY_SCOPE_LABEL[t] }))}
        />
        <TextFormField
          name="newSubjectId"
          label="Subject"
          required
          description="The id of the project, agent or organization that will own the memory."
          placeholder="Paste the id"
        />
        <FormActions submitLabel="Promote" onCancel={onClose} />
      </Form>
    </Modal>
  );
};
